import type React from 'react';
import { useState } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { toast } from 'react-toastify';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { API_BASE_URL } from '../../config';
import styles from './VulnerabilityDetail.module.css';

interface FixSuggestionsTabProps {
  vulnerabilityId: string;
  initialSuggestions: any[];
  schemaId?: string;
}

export const FixSuggestionsTab: React.FC<FixSuggestionsTabProps> = ({
  vulnerabilityId,
  initialSuggestions,
  schemaId,
}) => {
  const [fixSuggestions, setFixSuggestions] = useState<any[]>(initialSuggestions);
  const [isGenerating, setIsGenerating] = useState(false);

  const handleGenerate = async () => {
    setIsGenerating(true);
    try {
      let url = `${API_BASE_URL}/api/vulnerabilities/${vulnerabilityId}/ai-fix-suggestions`;
      if (schemaId) url += `?schema_id=${schemaId}`;
      const response = await fetch(url, {
        method: 'POST',
      });

      if (response.ok) {
        const result = await response.json();
        setFixSuggestions(result.data.suggestions);
        toast.success('Fix suggestions generated successfully');
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to generate fix suggestions');
      }
    } catch (error) {
      console.error('Error generating fix suggestions:', error);
      toast.error('Failed to generate fix suggestions');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div>
      <div className={styles.section}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1.5rem',
          }}
        >
          <h3 className={styles.sectionTitle} style={{ marginBottom: 0 }}>
            AI-Generated Fix Suggestions
          </h3>
          <button className={styles.actionButton} onClick={handleGenerate} disabled={isGenerating}>
            {isGenerating ? 'Generating...' : fixSuggestions.length > 0 ? 'Regenerate' : 'Generate Fix Suggestions'}
          </button>
        </div>

        {fixSuggestions.length === 0 ? (
          <div className={styles.emptyState}>
            <h3>No fix suggestions yet</h3>
            <p>Click &ldquo;Generate Fix Suggestions&rdquo; to get AI-powered remediation advice</p>
          </div>
        ) : (
          fixSuggestions.map((fix, index) => (
            <div className={styles.fixCard} key={index}>
              <div className={styles.fixHeader}>
                <h5 className={styles.fixTitle}>{fix.title}</h5>
                <span className={styles.complexityBadge} data-complexity={fix.complexity?.toLowerCase()}>
                  {fix.complexity} complexity
                </span>
              </div>
              <p className={styles.fixDescription}>{fix.description}</p>
              {fix.code_example && (
                <div className={styles.codeBlock}>
                  <SyntaxHighlighter language="javascript" style={vscDarkPlus} customStyle={{ fontSize: '0.875rem' }}>
                    {fix.code_example}
                  </SyntaxHighlighter>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};
